import React, { useEffect, useMemo, useState } from 'react';
import { posMenuAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, Trash2, Loader2, Save, Wine, ChevronUp, ChevronDown,
} from 'lucide-react';

const POSMenuManager = () => {
  const { toast } = useToast();
  const [categories, setCategories] = useState([]);
  const [drinks, setDrinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [newCat, setNewCat] = useState({ key: '', label: '', sort_order: 0 });
  const [newDrink, setNewDrink] = useState({ name: '', category: '', default_price: '', sort_order: 0 });

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    try {
      setLoading(true);
      const [cats, ds] = await Promise.all([posMenuAPI.listCategories(), posMenuAPI.listDrinks()]);
      setCategories(cats);
      setDrinks(ds);
    } catch (e) {
      toast({ title: 'Error loading POS menu', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const drinksByCat = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.key] = []; });
    drinks.forEach(d => {
      map[d.category] = map[d.category] || [];
      map[d.category].push(d);
    });
    Object.keys(map).forEach(k => map[k].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    return map;
  }, [categories, drinks]);

  // ──────────────── Drinks ────────────────
  const addDrink = async () => {
    if (!newDrink.name.trim() || !newDrink.category) {
      toast({ title: 'Name and category required', variant: 'destructive' });
      return;
    }
    try {
      await posMenuAPI.createDrink({
        ...newDrink,
        default_price: parseFloat(newDrink.default_price) || 0,
        sort_order: parseInt(newDrink.sort_order) || 0,
      });
      setNewDrink({ name: '', category: '', default_price: '', sort_order: 0 });
      toast({ title: 'Drink added' });
      await refresh();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Error adding drink', variant: 'destructive' });
    }
  };

  const saveDrink = async (drink, patch) => {
    setSavingId(drink.id);
    try {
      await posMenuAPI.updateDrink(drink.id, patch);
      await refresh();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally {
      setSavingId(null);
    }
  };

  const reorderDrink = async (drink, delta) => {
    const list = drinksByCat[drink.category] || [];
    const idx = list.findIndex(d => d.id === drink.id);
    const target = list[idx + delta];
    if (!target) return;
    await Promise.all([
      posMenuAPI.updateDrink(drink.id, { sort_order: target.sort_order }),
      posMenuAPI.updateDrink(target.id, { sort_order: drink.sort_order }),
    ]);
    await refresh();
  };

  const removeDrink = async (drink) => {
    if (!window.confirm(`Delete "${drink.name}"? Existing POS transactions referencing it are unaffected.`)) return;
    try {
      await posMenuAPI.deleteDrink(drink.id);
      toast({ title: 'Drink deleted' });
      await refresh();
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  // ──────────────── Categories ────────────────
  const addCategory = async () => {
    if (!newCat.key.trim() || !newCat.label.trim()) {
      toast({ title: 'Category key and label required', variant: 'destructive' });
      return;
    }
    try {
      await posMenuAPI.createCategory({
        ...newCat,
        sort_order: parseInt(newCat.sort_order) || (categories.length + 1),
      });
      setNewCat({ key: '', label: '', sort_order: 0 });
      toast({ title: 'Category added' });
      await refresh();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Error adding category', variant: 'destructive' });
    }
  };

  const saveCategory = async (cat, patch) => {
    try {
      await posMenuAPI.updateCategory(cat.id, patch);
      await refresh();
    } catch (e) {
      toast({ title: 'Save failed', variant: 'destructive' });
    }
  };

  const removeCategory = async (cat) => {
    const count = (drinksByCat[cat.key] || []).length;
    if (count > 0) {
      toast({
        title: `Cannot delete "${cat.label}"`,
        description: `${count} drink(s) still use this category. Move or delete them first.`,
        variant: 'destructive',
      });
      return;
    }
    if (!window.confirm(`Delete category "${cat.label}"?`)) return;
    try {
      await posMenuAPI.deleteCategory(cat.id);
      toast({ title: 'Category deleted' });
      await refresh();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Delete failed', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Wine className="text-[#1e3a5f]" size={28} />
          <h1 className="text-3xl font-bold text-[#1e3a5f]">POS Menu Manager</h1>
          <span className="text-sm text-gray-500 ml-2">
            Edits apply to NEW POS sessions. Already-open sessions keep their captured prices.
          </span>
        </div>

        {loading ? (
          <Card><CardContent className="py-12 text-center"><Loader2 className="animate-spin inline mr-2" />Loading…</CardContent></Card>
        ) : (
          <>
            {/* Categories */}
            <Card className="mb-6">
              <CardContent className="pt-4">
                <h2 className="text-lg font-bold text-[#1e3a5f] mb-3">Categories</h2>
                <div className="space-y-2">
                  {categories.map(cat => (
                    <CategoryRow key={cat.id} cat={cat} onSave={saveCategory} onDelete={removeCategory} drinkCount={(drinksByCat[cat.key] || []).length} />
                  ))}
                </div>
                {/* Add new */}
                <div className="mt-4 pt-4 border-t grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3">
                    <Label className="text-xs">Key</Label>
                    <Input value={newCat.key} onChange={(e) => setNewCat({ ...newCat, key: e.target.value })} placeholder="signature" data-testid="new-cat-key" />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Label</Label>
                    <Input value={newCat.label} onChange={(e) => setNewCat({ ...newCat, label: e.target.value })} placeholder="Signature Drinks" data-testid="new-cat-label" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Sort</Label>
                    <Input type="number" value={newCat.sort_order} onChange={(e) => setNewCat({ ...newCat, sort_order: e.target.value })} />
                  </div>
                  <div className="col-span-3">
                    <Button onClick={addCategory} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white w-full" data-testid="add-cat-btn">
                      <Plus size={16} className="mr-1" />Add Category
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Drinks grouped by category */}
            <Card>
              <CardContent className="pt-4">
                <h2 className="text-lg font-bold text-[#1e3a5f] mb-3">Drinks</h2>

                {categories.map(cat => (
                  <div key={cat.id} className="mb-5">
                    <h3 className="font-semibold text-[#1e3a5f] mb-2 border-b pb-1">{cat.label}</h3>
                    {(drinksByCat[cat.key] || []).length === 0 ? (
                      <p className="text-xs text-gray-400 italic py-2">No drinks in this category yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {(drinksByCat[cat.key] || []).map((d) => (
                          <DrinkRow
                            key={d.id}
                            drink={d}
                            categories={categories}
                            saving={savingId === d.id}
                            onSave={(patch) => saveDrink(d, patch)}
                            onDelete={() => removeDrink(d)}
                            onMoveUp={() => reorderDrink(d, -1)}
                            onMoveDown={() => reorderDrink(d, 1)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Add new drink */}
                <div className="mt-4 pt-4 border-t grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    <Label className="text-xs">Drink Name</Label>
                    <Input value={newDrink.name} onChange={(e) => setNewDrink({ ...newDrink, name: e.target.value })} placeholder="Mojito" data-testid="new-drink-name" />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Category</Label>
                    <Select value={newDrink.category} onValueChange={(v) => setNewDrink({ ...newDrink, category: v })}>
                      <SelectTrigger data-testid="new-drink-cat"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>
                        {categories.map(c => <SelectItem key={c.id} value={c.key}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Default $</Label>
                    <Input type="number" step="0.01" value={newDrink.default_price} onChange={(e) => setNewDrink({ ...newDrink, default_price: e.target.value })} placeholder="0.00" data-testid="new-drink-price" />
                  </div>
                  <div className="col-span-3">
                    <Button onClick={addDrink} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white w-full" data-testid="add-drink-btn">
                      <Plus size={16} className="mr-1" />Add Drink
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

/* ────────── Sub-components ────────── */
const DrinkRow = ({ drink, categories, saving, onSave, onDelete, onMoveUp, onMoveDown }) => {
  const [name, setName] = useState(drink.name);
  const [price, setPrice] = useState(drink.default_price);
  const [category, setCategory] = useState(drink.category);
  const [active, setActive] = useState(drink.active !== false);
  const dirty = name !== drink.name || parseFloat(price) !== drink.default_price || category !== drink.category || active !== (drink.active !== false);

  useEffect(() => {
    setName(drink.name); setPrice(drink.default_price); setCategory(drink.category); setActive(drink.active !== false);
  }, [drink]);

  return (
    <div className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded p-2" data-testid={`drink-row-${drink.id}`}>
      <div className="col-span-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="col-span-3">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {categories.map(c => <SelectItem key={c.id} value={c.key}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="text-right" />
      </div>
      <div className="col-span-1 flex items-center justify-center">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} title="Active (shown in POS)" />
      </div>
      <div className="col-span-2 flex items-center gap-1 justify-end">
        <Button size="sm" variant="ghost" onClick={onMoveUp} title="Move up"><ChevronUp size={14} /></Button>
        <Button size="sm" variant="ghost" onClick={onMoveDown} title="Move down"><ChevronDown size={14} /></Button>
        <Button
          size="sm"
          variant={dirty ? 'default' : 'outline'}
          disabled={!dirty || saving}
          onClick={() => onSave({ name, default_price: parseFloat(price) || 0, category, active })}
          className={dirty ? 'bg-[#1e3a5f] text-white' : ''}
          data-testid={`drink-save-${drink.id}`}
        >
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-600 hover:bg-red-50">
          <Trash2 size={14} />
        </Button>
      </div>
    </div>
  );
};

const CategoryRow = ({ cat, onSave, onDelete, drinkCount }) => {
  const [key, setKey] = useState(cat.key);
  const [label, setLabel] = useState(cat.label);
  const [sort, setSort] = useState(cat.sort_order);
  const dirty = key !== cat.key || label !== cat.label || parseInt(sort) !== cat.sort_order;

  useEffect(() => { setKey(cat.key); setLabel(cat.label); setSort(cat.sort_order); }, [cat]);

  return (
    <div className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded p-2" data-testid={`cat-row-${cat.id}`}>
      <div className="col-span-2">
        <Input value={key} onChange={(e) => setKey(e.target.value)} className="text-xs font-mono" title="Machine key (lowercase, underscores). Renaming cascades to all drinks in this category." />
      </div>
      <div className="col-span-5">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} />
      </div>
      <div className="col-span-2">
        <Input type="number" value={sort} onChange={(e) => setSort(e.target.value)} />
      </div>
      <div className="col-span-1 text-xs text-center text-gray-500">{drinkCount}</div>
      <div className="col-span-2 flex items-center gap-1 justify-end">
        <Button
          size="sm"
          variant={dirty ? 'default' : 'outline'}
          disabled={!dirty}
          onClick={() => onSave(cat, { key, label, sort_order: parseInt(sort) || 0 })}
          className={dirty ? 'bg-[#1e3a5f] text-white' : ''}
        >
          <Save size={14} />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(cat)} className="text-red-600 hover:bg-red-50">
          <Trash2 size={14} />
        </Button>
      </div>
    </div>
  );
};

export default POSMenuManager;
